// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import { apiErrorDefaultAction } from '../../../share/util/api/errorAction';
import { deviceDisconnect } from './deviceDisconnect';
import { startSSM4 } from './startSSM4';
import { PHASE } from './constants';

export default {
  name: 'startSSM4Modal',
  data() {
    return {
      modal_ssm4: false,
      isLoading: false,
      phase: PHASE.START,
    };
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      userProperty: state => state.userProperty,
      samlAccountList: state => state.systems.samlAccountList,
      currentProvider: state => state.provider.currentProvider,
      loggedIn: state => state.auth.loggedIn,
    }),

    /** ヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.DEVICE_DISCONNECT_ERROR:
        case PHASE.START_SSM4_ERROR:
          return 'danger';
        case PHASE.START:
        default:
          return 'secondary';
      }
    },

    /** ヘッダータイトル */
    headerTitle() {
      // 確認： SSM4起動確認
      const ConfirmationTitle = 'message.common_title_confirmation';
      // エラー： デバイス切断失敗、SSM4起動失敗
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        start: ConfirmationTitle,
        deviceDisconnectError: ErrorTitle,
        startSSM4Error: ErrorTitle,
      };
      return this.$t(titleMap[this.phase]);
    },

    /** 表示メッセージ */
    ssm4Message() {
      const messageMap = {
        start: 'message.message_start_ssm4_start_confirm',
        deviceDisconnectError: 'message.message_start_ssm4_device_disconnect_failed',
        startSSM4Error: 'message.message_start_ssm4_start_failed',
      };
      return this.$t(messageMap[this.phase]);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('fileResources', ['checkSBRUser']),

    /** 初期化処理 */
    init() {
      this.modal_ssm4 = true;
    },

    /** ダイアログを閉じる */
    async closeSSM4Modal() {
      this.$emit('close-wizard');
      this.modal_ssm4 = false;
    },

    /** OKボタン押下処理 */
    async onOkClicked() {
      // デバイス切断処理
      await this.deviceDisconnectProcess();
    },

    /** デバイス切断処理 */
    async deviceDisconnectProcess() {
      // デバイス切断API実行
      await deviceDisconnect(this);
    },

    /** SSM4実行ファイル起動処理 */
    async startSSM4Process() {
      // SSM4実行ファイル起動API実行
      await startSSM4(this);
    },

    /**
     * SSM5 完全ログアウト処理
     * ※エラー遷移とは違い、意図的なログアウトのため、SSM5画面右上のプルダウンログアウトと同様の、完全ログアウトを実行する。
     */
    async SSM5Logout() {
      // Menu.jsのtapLogoutを呼び出す
      this.$emit('tapLogout', false);
    },

    //----- 異常コールバックメソッド -----------------------------------------------//
    /**
     * エラー発生時の表示
     * @param {string} errorPhase エラー発生フェーズ
     */
    showUnexpectedError(errorPhase) {
      switch (errorPhase) {
        case PHASE.DEVICE_DISCONNECT_ERROR:
          // 異常終了：デバイス切断失敗表示
          this.phase = PHASE.DEVICE_DISCONNECT_ERROR;
          break;
        case PHASE.START_SSM4_ERROR:
          // 異常終了：SSM4起動失敗表示
          this.phase = PHASE.START_SSM4_ERROR;
          break;
        default:
      }
    },

    /**
     * API 通信共通バリデーションエラー発生のコールバック
     * @param {object} result バリデーション結果
     * @param {string} errorPhase エラー発生フェーズ
     */
    validateErrorResult(result, errorPhase) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(errorPhase); },
      );
    },
  },

  mounted() {
    this.init();
  },
};